import type { EarTrainingClip, EarTrainingMode } from '../types/earTraining';
import { midiToSolfege } from './solfege';
import { tonicToPitchClass } from './pitchLabel';

const PRIME = '\u2032';
const LOW_MARK = '\u2193';

/** Movable-do pitch class for the syllable "do". */
export function doPitchClass(tonic: string, mode: EarTrainingMode): number {
  const tonicPc = tonicToPitchClass(tonic);
  if (mode === 'major') return tonicPc;
  return (tonicPc + 3) % 12;
}

/** Register anchor: do in the octave at or below the first sounding note. */
export function registerDoMidi(firstMidi: number, tonic: string, mode: EarTrainingMode): number {
  const doPc = doPitchClass(tonic, mode);
  const octave = Math.floor(firstMidi / 12);
  let doMidi = octave * 12 + doPc;
  if (doMidi > firstMidi) doMidi -= 12;
  return doMidi;
}

/** Tonic pitch in the octave at or below the first sounding note (key root for playback). */
export function registerTonicMidi(firstMidi: number, tonic: string): number {
  const tonicPc = tonicToPitchClass(tonic);
  const octave = Math.floor(firstMidi / 12);
  let midi = octave * 12 + tonicPc;
  if (midi > firstMidi) midi -= 12;
  return midi;
}

export function solfegeOctaveOffset(midi: number, anchorDoMidi: number): number {
  return Math.floor((midi - anchorDoMidi) / 12);
}

export function formatSolfegeToken(syllable: string, octaveOffset: number): string {
  if (octaveOffset === 0) return syllable;
  if (octaveOffset > 0) return syllable + PRIME.repeat(octaveOffset);
  return syllable + LOW_MARK.repeat(-octaveOffset);
}

export function parseSolfegeToken(token: string): { syllable: string; octaveOffset: number } {
  let s = token.trim().toLowerCase();
  let octaveOffset = 0;
  while (s.endsWith(PRIME) || s.endsWith("'")) {
    s = s.slice(0, -1);
    octaveOffset++;
  }
  while (s.endsWith(LOW_MARK)) {
    s = s.slice(0, -1);
    octaveOffset--;
  }
  return { syllable: s, octaveOffset };
}

export function midiToSolfegeToken(
  midi: number,
  tonic: string,
  mode: EarTrainingMode,
  keySignatureFifths: number,
  anchorDoMidi: number
): string {
  const syllable = midiToSolfege(midi, tonic, mode, keySignatureFifths);
  const octaveOffset = solfegeOctaveOffset(midi, anchorDoMidi);
  return formatSolfegeToken(syllable, octaveOffset);
}

export function anchorDoMidiForClip(clip: EarTrainingClip): number {
  const firstMidi = clip.notes[0]!.midi + clip.transposeSemitones;
  return registerDoMidi(firstMidi, clip.tonic, clip.mode);
}

/** True when the token includes ′ / ' / ↓ octave marks. */
export function tokenHasOctaveMark(token: string): boolean {
  const t = token.trim().toLowerCase();
  return /[\u2032'\u2193]/.test(t);
}

function tokenMatchesMidi(
  userToken: string,
  midi: number,
  clip: EarTrainingClip,
  anchorDoMidi: number
): boolean {
  const got = midiToSolfegeToken(
    midi,
    clip.tonic,
    clip.mode,
    clip.keySignatureFifths,
    anchorDoMidi
  );
  const user = parseSolfegeToken(userToken);
  const parsed = parseSolfegeToken(got);
  if (user.syllable !== parsed.syllable) return false;
  if (tokenHasOctaveMark(userToken)) {
    return user.octaveOffset === parsed.octaveOffset;
  }
  return true;
}

/** Sounding MIDI for a solfege token in the clip key (inverse of midiToSolfegeToken). */
export function solfegeTokenToMidi(
  token: string,
  clip: EarTrainingClip,
  anchorDoMidi = anchorDoMidiForClip(clip)
): number | null {
  const { syllable, octaveOffset } = parseSolfegeToken(token);
  const want = formatSolfegeToken(syllable, octaveOffset);

  for (let octaveShift = -2; octaveShift <= 3; octaveShift++) {
    const base = anchorDoMidi + octaveShift * 12;
    for (let delta = 0; delta < 12; delta++) {
      const midi = base + delta;
      const got = midiToSolfegeToken(
        midi,
        clip.tonic,
        clip.mode,
        clip.keySignatureFifths,
        anchorDoMidi
      );
      if (got === want) return midi;
    }
  }
  return null;
}

/**
 * Resolve a token to sounding MIDI near a reference pitch (same register as the melody).
 * Unmarked syllables (e.g. "do") match pitch class and pick the octave closest to refMidi.
 */
export function solfegeTokenToMidiNear(
  token: string,
  clip: EarTrainingClip,
  refMidi: number,
  anchorDoMidi = anchorDoMidiForClip(clip)
): number | null {
  let best: number | null = null;
  let bestDist = Infinity;

  for (let midi = refMidi - 18; midi <= refMidi + 18; midi++) {
    if (midi < 0 || midi > 127) continue;
    if (!tokenMatchesMidi(token, midi, clip, anchorDoMidi)) continue;
    const dist = Math.abs(midi - refMidi);
    if (dist < bestDist) {
      bestDist = dist;
      best = midi;
    }
  }
  return best;
}
